import {
  Button,
  EmptyState,
  FilterSegments,
  PageHeader,
  PageTitle,
  SearchInput,
  Select,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  formatDate,
  type FilterSegmentOption,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { useDebounced } from '@/lib/use-debounced'
import { useStoredFilter } from '@/lib/use-stored-filter'
import { routes } from '@/routes'
import { useTemplates, type Audience, type Category, type TemplateFilter } from './api'
import {
  AUDIENCE_CATEGORIES,
  AUDIENCE_LABELS,
  CATEGORY_LABELS,
  legacyUsage,
  templateStatus,
} from './labels'

/**
 * Contratos e termos: a lista (docs/areas/documentos-e-assinaturas.md, "Contratos e termos").
 * Arquétipo: lista de trabalho. Abre em "Por publicar" enquanto houver documento com alteração
 * salva que ainda não virou versão: é o trabalho pendente da tela.
 */

const FILTERS: TemplateFilter[] = ['unpublished', 'review', 'published', 'archived', 'all']
const AUDIENCES = Object.keys(AUDIENCE_LABELS) as Audience[]

export function TemplatesPage() {
  const navigate = useNavigate()
  const stored = useStoredFilter('templates', FILTERS, 'published')
  const [search, setSearch] = useState('')
  const [audience, setAudience] = useState<Audience | ''>('')
  const [category, setCategory] = useState<Category | ''>('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounced(search.trim())

  // Abre no que tem pendência, até a pessoa escolher um segmento (como em Procedimentos).
  const [unpublishedSeen, setUnpublishedSeen] = useState(0)
  const filter: TemplateFilter =
    !stored.hasChosen && unpublishedSeen > 0 ? 'unpublished' : stored.filter
  const query = useTemplates({
    filter,
    search: debouncedSearch || undefined,
    audience,
    category,
    page,
  })
  const counts = query.data?.meta.counts
  const unpublished = counts?.unpublished ?? 0
  if (unpublished !== unpublishedSeen) setUnpublishedSeen(unpublished)

  // A fila da revisão jurídica só aparece enquanto há o que revisar (ou enquanto está escolhida).
  const showReview = (counts?.review ?? 0) > 0 || filter === 'review'
  const options: FilterSegmentOption<TemplateFilter>[] = [
    { value: 'unpublished', label: 'Por publicar', count: counts?.unpublished },
    ...(showReview
      ? [{ value: 'review' as const, label: 'Sem revisão jurídica', count: counts?.review }]
      : []),
    { value: 'published', label: 'Publicados', count: counts?.published },
    { value: 'archived', label: 'Arquivados', count: counts?.archived },
    { value: 'all', label: 'Todos', count: counts?.all },
  ]

  const templates = query.data?.data ?? []
  const isEmptyCatalog = !query.isPending && (counts?.all ?? 0) === 0
  const hasNarrowing = Boolean(debouncedSearch || audience || category)
  const categories = audience ? AUDIENCE_CATEGORIES[audience] : AUDIENCE_CATEGORIES.patient

  function reset() {
    setSearch('')
    setAudience('')
    setCategory('')
    setPage(1)
  }

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Westhetics' }, { label: 'Contratos e termos' }]}
        actions={
          <Button size="sm" asChild>
            <Link to={routes.admin.newTemplate}>+ Novo documento</Link>
          </Button>
        }
      />
      <Page wide>
        <PageTitle
          title="Contratos e termos"
          subtitle={
            counts
              ? `${counts.all} ${counts.all === 1 ? 'documento' : 'documentos'} · ${
                  unpublished === 0
                    ? 'nenhuma alteração por publicar'
                    : `${unpublished} com alterações por publicar`
                }`
              : 'Os modelos que viram documento em todas as clínicas.'
          }
        />

        {query.isError ? (
          <LoadError
            className="mt-6"
            title="Não conseguimos carregar os contratos e termos"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : isEmptyCatalog && !hasNarrowing ? (
          <EmptyState
            className="mt-6"
            title="Nenhum contrato ou termo ainda"
            description="É daqui que sai todo documento que uma clínica emite. Cada um é escrito no editor, revisado e publicado em versões: o que a paciente assina fica ligado à versão exata."
            action={
              <Button asChild>
                <Link to={routes.admin.newTemplate}>Escrever o primeiro documento</Link>
              </Button>
            }
          />
        ) : (
          <>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <FilterSegments
                label="Filtrar documentos"
                options={options}
                value={filter}
                onChange={(value) => {
                  stored.setFilter(value)
                  setPage(1)
                }}
              />
            </div>
            <div className="tablet:grid-cols-[200px_240px_minmax(0,1fr)] mt-3 grid grid-cols-1 gap-3">
              <Select
                aria-label="Público"
                value={audience}
                onChange={(event) => {
                  setAudience(event.target.value as Audience | '')
                  setCategory('')
                  setPage(1)
                }}
              >
                <option value="">Todos os públicos</option>
                {AUDIENCES.map((value) => (
                  <option key={value} value={value}>
                    {AUDIENCE_LABELS[value]}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="Categoria"
                value={category}
                onChange={(event) => {
                  setCategory(event.target.value as Category | '')
                  setPage(1)
                }}
              >
                <option value="">Todas as categorias</option>
                {categories.map((value) => (
                  <option key={value} value={value}>
                    {CATEGORY_LABELS[value]}
                  </option>
                ))}
              </Select>
              <SearchInput
                className="min-w-0"
                value={search}
                onChange={(value) => {
                  setSearch(value)
                  setPage(1)
                }}
                placeholder="Buscar por nome ou procedimento"
                aria-label="Buscar documento"
              />
            </div>

            <div className="mt-4">
              {query.isPending ? (
                <TableSkeleton rows={6} columns={4} />
              ) : templates.length === 0 ? (
                <EmptyState
                  title={
                    hasNarrowing
                      ? 'Nenhum documento com estes filtros'
                      : filter === 'unpublished'
                        ? 'Nada por publicar'
                        : filter === 'review'
                          ? 'Nada sem revisão jurídica'
                          : filter === 'archived'
                            ? 'Nenhum documento arquivado'
                            : 'Nenhum documento publicado'
                  }
                  description={
                    hasNarrowing
                      ? 'A busca cobre o nome do documento e o procedimento a que ele se aplica.'
                      : filter === 'unpublished'
                        ? 'Todo documento está igual à versão que as clínicas usam. Uma alteração salva no editor aparece aqui até virar versão.'
                        : filter === 'review'
                          ? 'Todo documento que veio do sistema anterior já foi publicado com a revisão jurídica registrada.'
                          : filter === 'archived'
                            ? 'O que for arquivado sai das escolhas das clínicas e aparece aqui; volta a qualquer momento.'
                            : 'Um documento só chega às clínicas depois de publicado.'
                  }
                  action={
                    hasNarrowing ? (
                      <Button variant="outline" onClick={reset}>
                        Limpar filtros
                      </Button>
                    ) : (
                      <Button variant="outline" onClick={() => stored.setFilter('all')}>
                        Ver todos
                      </Button>
                    )
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHead>Documento</TableHead>
                      <TableHead>Versão</TableHead>
                      <TableHead numeric>Procedimentos</TableHead>
                      <TableHead>Status</TableHead>
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {templates.map((template) => {
                      const status = templateStatus(template)
                      const version = template.currentVersion
                      return (
                        <TableRow
                          key={template.id}
                          onClick={() => navigate(routes.admin.template(template.id))}
                        >
                          <TableCell primary>
                            <div className="min-w-0">
                              <Link
                                to={routes.admin.template(template.id)}
                                onClick={(event) => event.stopPropagation()}
                              >
                                {template.name}
                              </Link>
                              <div className="font-body text-secondary mt-1 text-[12px] leading-[1.5] font-light">
                                {[
                                  CATEGORY_LABELS[template.category],
                                  AUDIENCE_LABELS[template.audience],
                                  legacyUsage(template),
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell label="Versão" muted>
                            {version
                              ? `${version.label} · ${formatDate(new Date(version.publishedAt), { year: true })}`
                              : 'nunca publicado'}
                          </TableCell>
                          <TableCell label="Procedimentos" numeric muted>
                            {template.procedures.length}
                          </TableCell>
                          <TableCell label="Status">
                            <StatusTag level={status.level}>{status.label}</StatusTag>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
              {query.data && templates.length > 0 ? (
                <ListFooter
                  meta={query.data.meta}
                  noun={['documento', 'documentos']}
                  note={
                    filter === 'unpublished'
                      ? 'a alteração mais recente primeiro'
                      : filter === 'review'
                        ? 'o que já vale nas clínicas primeiro, depois o mais usado no sistema anterior'
                        : 'em ordem alfabética'
                  }
                  onPage={setPage}
                />
              ) : null}
            </div>
          </>
        )}
      </Page>
    </>
  )
}
